"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const libsodium_wrappers_1 = __importDefault(require("libsodium-wrappers"));
const events_1 = require("events");
const simple_peer_1 = __importDefault(require("simple-peer"));
const types_1 = require("./types");
const util_1 = require("./util");
const DEBUG = process.env.NODE_ENV !== 'production';
class SignalClient extends events_1.EventEmitter {
    constructor(ws, opts) {
        super();
        this.ws = ws;
        this.connectingPeers = {};
        this.simplePeerOpts = opts.peerOpts || {};
        this.connectTimeout = opts.connectTimeout || 15e3;
        this.onConnect = this.onConnect.bind(this);
        this.onDisconnect = this.onDisconnect.bind(this);
        this.onError = this.onError.bind(this);
        this.onMessage = this.onMessage.bind(this);
        this.onOfferReceived = this.onOfferReceived.bind(this);
        this.ws.addEventListener('open', this.onConnect);
        this.ws.addEventListener('close', this.onDisconnect);
        this.ws.addEventListener('error', this.onError);
        this.ws.addEventListener('message', this.onMessage);
    }
    get connected() {
        return this.ws.readyState === 1; /* Open */
    }
    close() {
        if (this.ws.readyState < 2) {
            this.ws.close();
        }
        for (const peer of Object.values(this.connectingPeers)) {
            if (peer)
                peer.destroy();
        }
        this.connectingPeers = {};
    }
    send(data) {
        this.ws.send(JSON.stringify(data));
    }
    ping() {
        this.send({ t: 6 /* Ping */ });
    }
    onConnect() {
        this.emit('connect');
    }
    onDisconnect() {
        this.ws.removeEventListener('open', this.onConnect);
        this.ws.removeEventListener('close', this.onDisconnect);
        this.ws.removeEventListener('message', this.onMessage);
        this.ws.removeEventListener('error', this.onError);
        this.close();
        this.emit('close');
    }
    onError(err) {
        this.emit('error', err);
        this.close();
    }
    onMessage(event) {
        const msg = event.data;
        let req;
        try {
            req = JSON.parse(msg.toString());
        }
        catch (_a) {
            console.error(`[SignalClient] Invalid request`, msg);
            return;
        }
        switch (req.t) {
            case 3 /* AuthChallenge */:
                this.emit('challenge', req.c);
                break;
            case 1 /* CreateRoomSuccess */:
                this.emit('create-room-success');
                break;
            case 5 /* CandidateOffer */:
                this.emit('offer', req.o, req.f);
                break;
            case 8 /* RoomNotFound */:
                const err = new Error();
                err.code = types_1.SignalErrorCode.RoomNotFound;
                this.onError(err);
            default:
                break;
        }
    }
    createPeer(options) {
        const peer = new simple_peer_1.default({ ...this.simplePeerOpts, ...options });
        // Ignore invalid ICE candidate errors
        // Chrome v75 and Firefox v68 have an incompatibility with trickle ICE
        // https://github.com/feross/simple-peer/issues/503
        peer.destroy = (err) => {
            if (typeof err === 'object' && err.code === 'ERR_ADD_ICE_CANDIDATE')
                return;
            peer._destroy(err, () => { });
        };
        return peer;
    }
    async createRoom(keyPair) {
        this.send({
            t: 0 /* CreateRoom */,
            id: libsodium_wrappers_1.default.to_hex(keyPair.publicKey)
        });
        const [challenge] = await util_1.waitEvent(this, 'challenge');
        this.solveChallenge(keyPair, challenge);
        await util_1.waitEvent(this, 'create-room-success');
        this.on('offer', this.onOfferReceived);
        this.once('close', () => {
            this.removeListener('offer', this.onOfferReceived);
        });
    }
    async joinRoom(id) {
        const peer = this.createPeer({ initiator: true });
        const [initialOffer] = await util_1.waitEvent(peer, 'signal');
        this.send({
            t: 2 /* JoinRoom */,
            id,
            o: initialOffer
        });
        const onSignal = (offer) => {
            this.send({
                t: 5 /* CandidateOffer */,
                o: offer
            });
        };
        const onOffer = (offer) => {
            peer.signal(offer);
        };
        peer.once('connect', () => {
            peer.removeListener('signal', onSignal);
            this.removeListener('offer', onOffer);
            this.emit('peer', peer);
        });
        peer.on('signal', onSignal);
        this.on('offer', onOffer);
        const connectPromises = [
            util_1.waitEvent(this, 'peer', this.connectTimeout),
            util_1.waitEvent(peer, 'error', this.connectTimeout),
            util_1.waitEvent(this, 'error', this.connectTimeout),
            util_1.waitEvent(this, 'close', this.connectTimeout)
        ];
        try {
            const [result] = await Promise.race(connectPromises);
            if (result instanceof Error) {
                throw result;
            }
            else if (!(result instanceof simple_peer_1.default)) {
                throw new Error('Failed to join room');
            }
        }
        catch (e) {
            peer.destroy();
            throw e;
        }
        finally {
            connectPromises.forEach(p => p.cancel());
            peer.removeListener('signal', onSignal);
            this.removeListener('offer', onOffer);
        }
        return peer;
    }
    solveChallenge(keyPair, data) {
        const challenge = libsodium_wrappers_1.default.from_base64(data, libsodium_wrappers_1.default.base64_variants.URLSAFE_NO_PADDING);
        const nonce = libsodium_wrappers_1.default.crypto_box_seal_open(challenge, keyPair.publicKey, keyPair.privateKey);
        const decoded = libsodium_wrappers_1.default.to_base64(nonce, libsodium_wrappers_1.default.base64_variants.URLSAFE_NO_PADDING);
        this.send({
            t: 4 /* AuthResponse */,
            c: decoded
        });
    }
    findOrCreatePeer(clientId) {
        let peer = this.connectingPeers[clientId];
        if (peer)
            return peer;
        peer = this.createPeer();
        this.connectingPeers[clientId] = peer;
        // Clear ref on disconnect
        const onClose = () => (this.connectingPeers[clientId] = undefined);
        // Forward signal data to peer
        const onSignal = (offer) => {
            this.send({
                t: 5 /* CandidateOffer */,
                o: offer,
                to: clientId
            });
        };
        peer.on('signal', onSignal);
        peer.once('close', onClose);
        peer.once('connect', () => {
            this.connectingPeers[clientId] = undefined;
            peer.removeListener('close', onClose);
            peer.removeListener('signal', onSignal);
            this.emit('peer', peer);
        });
        return peer;
    }
    onOfferReceived(offer, from) {
        const peer = this.findOrCreatePeer(from);
        peer.signal(offer);
    }
}
exports.SignalClient = SignalClient;
exports.default = async (opts) => {
    // TODO: validate keys
    await libsodium_wrappers_1.default.ready;
    const ws = new (opts.WebSocket || WebSocket)(opts.server);
    const client = new SignalClient(ws, opts);
    await new Promise((resolve, reject) => {
        const cleanup = () => {
            client.removeListener('connect', connect);
            client.removeListener('error', error);
        };
        const connect = () => {
            cleanup();
            resolve();
        };
        const error = (err) => {
            cleanup();
            reject(err);
        };
        client.on('connect', connect);
        client.on('error', error);
    });
    return client;
};
//# sourceMappingURL=client.js.map